import React, { useEffect, useMemo, useRef, useState } from "react";

import { apiFetch } from "./api";
import PreviewPane from "./PreviewPane";
import WorkflowStatusPanel from "./WorkflowStatusPanel";
import {
  buildPatch,
  fieldUsesTextarea,
  getArrayTemplate,
  getIn,
  readFileAsDataUrl,
  removeAt,
  resolveSiteAssetUrl,
  setIn,
  slugify,
  toLabel,
} from "./contentUtils";

export const SECTION_ORDER = [
  "site",
  "seo",
  "hero",
  "intro",
  "services",
  "about",
  "usp",
  "testimonials",
  "faq",
  "openingHours",
  "contact",
  "footer",
  "media",
];

const SORTABLE_ARRAYS = new Set([
  "services.items",
  "usp.items",
  "faq.items",
  "media.gallery",
]);

const WEEKDAYS = [
  "Måndag",
  "Tisdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lördag",
  "Söndag",
];

const LONG_PRESS_MS = 260;
const TOUCH_DRAG_CANCEL_DISTANCE = 10;
const NEW_ITEM_HIGHLIGHT_MS = 2400;
const THEME_OPTIONS = [
  { value: "classic", label: "Klassisk" },
  { value: "editorial", label: "Editorial" },
  { value: "showcase", label: "Showcase" },
];
const THEME_MODE_OPTIONS = [
  { value: "light", label: "Ljust läge" },
  { value: "dark", label: "Mörkt läge" },
];

const FONT_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: '"Segoe UI", Arial, sans-serif', label: 'Segoe UI' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", Arial, sans-serif', label: 'Trebuchet MS' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
];

const COLOR_PICKER_FALLBACKS = {
  "site.headerBackgroundColor": "#fcfbf7",
  "site.mainBackgroundColor": "#f7f5ef",
  "site.footerBackgroundColor": "#172033",
};

export const SECTION_LABELS = {
  site: "Webbplats",
  seo: "SEO",
  hero: "Hero",
  intro: "Intro",
  services: "Tjänster",
  about: "Om oss",
  usp: "USP",
  testimonials: "Omdömen",
  faq: "FAQ",
  openingHours: "Öppettider",
  contact: "Kontakt",
  footer: "Footer",
  media: "Media",
};

export const SECTION_VISIBILITY_PATHS = {
  hero: "hero.enabled",
  intro: "intro.enabled",
  services: "services.enabled",
  about: "about.enabled",
  usp: "usp.enabled",
  testimonials: "testimonials.enabled",
  faq: "faq.enabled",
  openingHours: "openingHours.enabled",
  contact: "contact.enabled",
  footer: "footer.enabled",
  media: "media.galleryEnabled",
};

const SECTION_FOCUS_PATHS = {
  site: "site.displayName",
  seo: "seo.title",
  hero: "hero.eyebrow",
  intro: "intro.eyebrow",
  services: "services.eyebrow",
  about: "about.eyebrow",
  usp: "usp.heading",
  testimonials: "testimonials.eyebrow",
  faq: "faq.eyebrow",
  openingHours: "openingHours.eyebrow",
  contact: "contact.eyebrow",
  footer: "footer.companyName",
  media: "media.galleryEyebrow",
};

function moveItem(array, fromIndex, toIndex) {
  if (
    !Array.isArray(array) ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0
  ) {
    return array;
  }

  const next = [...array];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

function isInteractiveTarget(target) {
  return (
    target instanceof HTMLElement &&
    Boolean(target.closest("input, textarea, button, label, select, option, a"))
  );
}

function buildArrayItemKey(pathKey, index) {
  return `${pathKey}:${index}`;
}

function getNewItemFocusPath(arrayPath, index, item) {
  if (typeof item === "string") {
    return `${arrayPath}.${index}`;
  }

  if (arrayPath === "media.gallery") {
    return `${arrayPath}.${index}.url`;
  }

  const firstKey = item && typeof item === "object" ? Object.keys(item)[0] : "";
  return firstKey
    ? `${arrayPath}.${index}.${firstKey}`
    : `${arrayPath}.${index}`;
}

function buildSocialLinkKey(index, existingLinks) {
  const baseKey = `lank${index + 1}`;
  if (!Object.prototype.hasOwnProperty.call(existingLinks, baseKey)) {
    return baseKey;
  }

  let suffix = index + 2;
  while (Object.prototype.hasOwnProperty.call(existingLinks, `${baseKey}${suffix}`)) {
    suffix += 1;
  }
  return `${baseKey}${suffix}`;
}

export default function ContentEditor({
  site,
  accessToken,
  activeSection = SECTION_ORDER[0],
  onActiveSectionChange,
  onSectionVisibilityChange,
}) {
  const fieldRefs = useRef(new Map());
  const arrayItemRefs = useRef(new Map());
  const pendingRevealRef = useRef(null);
  const revealTimeoutRef = useRef(null);
  const touchDragMetaRef = useRef(null);
  const lastSectionFocusRef = useRef("");
  const [originalContent, setOriginalContent] = useState(null);
  const [draftContent, setDraftContent] = useState(null);
  const [meta, setMeta] = useState(null);
  const [previewSources, setPreviewSources] = useState({});
  const [activeFieldPath, setActiveFieldPath] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [publishState, setPublishState] = useState({
    status: "idle",
    message: "",
  });
  const [trackedCommitSha, setTrackedCommitSha] = useState("");
  const [uploading, setUploading] = useState({});
  const [dragState, setDragState] = useState(null);
  const [highlightedArrayItemKey, setHighlightedArrayItemKey] = useState("");
  const resolvedActiveSection = SECTION_ORDER.includes(activeSection)
    ? activeSection
    : SECTION_ORDER[0];
  const sectionVisibility = useMemo(() => {
    if (!draftContent) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(SECTION_VISIBILITY_PATHS).map(([sectionKey, pathKey]) => [
        sectionKey,
        getIn(draftContent, pathKey.split(".")) !== false,
      ]),
    );
  }, [draftContent]);

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
      }

      if (touchDragMetaRef.current?.timerId) {
        window.clearTimeout(touchDragMetaRef.current.timerId);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setPublishState({ status: "idle", message: "" });
    setTrackedCommitSha("");
    setPreviewSources({});
    setActiveFieldPath("");
    lastSectionFocusRef.current = "";
    setDragState(null);
    setHighlightedArrayItemKey("");
    pendingRevealRef.current = null;

    apiFetch(`/sites/${site.site_id}/content/home`, { accessToken })
      .then((payload) => {
        if (!active) {
          return;
        }

        setOriginalContent(payload.data);
        setDraftContent(payload.data);
        setMeta(payload.meta);
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message || "Kunde inte ladda innehållet.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [accessToken, site.site_id]);

  useEffect(() => {
    if (!pendingRevealRef.current) {
      return;
    }

    const { itemKey, focusPath } = pendingRevealRef.current;
    const element = arrayItemRefs.current.get(itemKey);

    if (!element) {
      return;
    }

    pendingRevealRef.current = null;

    if (revealTimeoutRef.current) {
      window.clearTimeout(revealTimeoutRef.current);
    }

    setHighlightedArrayItemKey(itemKey);
    element.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });

    window.setTimeout(() => {
      const focusElement = fieldRefs.current.get(focusPath);
      if (focusElement) {
        setActiveFieldPath(focusPath);
        focusElement.focus({ preventScroll: true });
      }
    }, 180);

    revealTimeoutRef.current = window.setTimeout(() => {
      setHighlightedArrayItemKey((current) =>
        current === itemKey ? "" : current,
      );
    }, NEW_ITEM_HIGHLIGHT_MS);
  }, [draftContent]);

  useEffect(() => {
    onSectionVisibilityChange?.(sectionVisibility);
  }, [onSectionVisibilityChange, sectionVisibility]);

  useEffect(() => {
    if (!draftContent) {
      return;
    }

    if (lastSectionFocusRef.current === resolvedActiveSection) {
      return;
    }

    lastSectionFocusRef.current = resolvedActiveSection;
    setActiveFieldPath(SECTION_FOCUS_PATHS[resolvedActiveSection] || "");
  }, [draftContent, resolvedActiveSection]);

  const changedPatch = useMemo(
    () =>
      originalContent && draftContent
        ? buildPatch(originalContent, draftContent)
        : undefined,
    [originalContent, draftContent],
  );

  const hasChanges = Boolean(changedPatch && Object.keys(changedPatch).length);

  function updateValue(path, value) {
    setDraftContent((current) => setIn(current, path, value));
  }

  function registerArrayItemRef(itemKey, element) {
    if (!element) {
      arrayItemRefs.current.delete(itemKey);
      return;
    }

    arrayItemRefs.current.set(itemKey, element);
  }

  function addArrayItem(path) {
    const arrayPath = path.join(".");
    const currentArray = getIn(draftContent, path) || [];
    const nextItem = getArrayTemplate(arrayPath);
    const nextIndex = currentArray.length;

    pendingRevealRef.current = {
      itemKey: buildArrayItemKey(arrayPath, nextIndex),
      focusPath: getNewItemFocusPath(arrayPath, nextIndex, nextItem),
    };

    updateValue(path, [...currentArray, nextItem]);
  }

  function removeArrayItem(path, index) {
    const currentArray = getIn(draftContent, path) || [];
    updateValue(path, removeAt(currentArray, index));
  }

  function moveArrayItem(path, fromIndex, toIndex) {
    const currentArray = getIn(draftContent, path) || [];
    const nextArray = moveItem(currentArray, fromIndex, toIndex);
    updateValue(path, nextArray);
  }

  function isSortableArray(pathKey) {
    return SORTABLE_ARRAYS.has(pathKey);
  }

  function handleDragStart(pathKey, index) {
    setDragState({
      pathKey,
      fromIndex: index,
      overIndex: index,
      mode: "mouse",
    });
  }

  function handleDragEnter(pathKey, index) {
    setDragState((current) => {
      if (!current || current.pathKey !== pathKey) {
        return current;
      }

      if (current.overIndex === index) {
        return current;
      }

      return { ...current, overIndex: index };
    });
  }

  function handleDrop(path, pathKey, index) {
    setDragState((current) => {
      if (!current || current.pathKey !== pathKey) {
        return null;
      }

      if (current.fromIndex !== index) {
        moveArrayItem(path, current.fromIndex, index);
      }

      return null;
    });
  }

  function addSocialLink(path) {
    const currentLinks = getIn(draftContent, path) || {};
    const nextKey = buildSocialLinkKey(Object.keys(currentLinks).length, currentLinks);
    updateValue(path, {
      ...currentLinks,
      [nextKey]: "",
    });

    window.setTimeout(() => {
      const focusPath = [...path, nextKey].join(".");
      const element = fieldRefs.current.get(`${focusPath}:url`);
      if (element) {
        setActiveFieldPath(focusPath);
        element.focus({ preventScroll: true });
      }
    }, 80);
  }

  function updateSocialLinks(path, entries) {
    updateValue(path, Object.fromEntries(entries));
  }

  function renameSocialLink(path, oldKey, nextKey) {
    const trimmedKey = String(nextKey || "").trim();
    if (!trimmedKey || trimmedKey === oldKey) {
      return;
    }

    const currentLinks = getIn(draftContent, path) || {};
    const entries = Object.entries(currentLinks).map(([key, url]) =>
      key === oldKey ? [trimmedKey, url] : [key, url],
    );
    updateSocialLinks(path, entries);
  }

  function updateSocialLinkUrl(path, key, nextUrl) {
    const currentLinks = getIn(draftContent, path) || {};
    updateValue(path, {
      ...currentLinks,
      [key]: nextUrl,
    });
  }

  function removeSocialLink(path, key) {
    const currentLinks = getIn(draftContent, path) || {};
    updateSocialLinks(
      path,
      Object.entries(currentLinks).filter(([currentKey]) => currentKey !== key),
    );
  }

  function handleSocialLinkDrop(path, pathKey, index) {
    setDragState((current) => {
      if (!current || current.pathKey !== pathKey) {
        return null;
      }

      if (current.fromIndex !== index) {
        const currentEntries = Object.entries(getIn(draftContent, path) || {});
        updateSocialLinks(path, moveItem(currentEntries, current.fromIndex, index));
      }

      return null;
    });
  }

  function releasePointerCapture(meta) {
    if (
      !meta?.element ||
      meta.pointerId == null ||
      typeof meta.element.releasePointerCapture !== "function"
    ) {
      return;
    }

    if (meta.element.hasPointerCapture?.(meta.pointerId)) {
      meta.element.releasePointerCapture(meta.pointerId);
    }
  }

  function clearTouchDrag() {
    const meta = touchDragMetaRef.current;

    if (meta?.timerId) {
      window.clearTimeout(meta.timerId);
    }

    releasePointerCapture(meta);
    touchDragMetaRef.current = null;
    setDragState((current) => (current?.mode === "touch" ? null : current));
  }

  function handleArrayItemPointerDown(path, pathKey, index, event) {
    if (event.pointerType !== "touch" || isInteractiveTarget(event.target)) {
      return;
    }

    clearTouchDrag();

    const element = event.currentTarget;
    element.setPointerCapture?.(event.pointerId);

    const meta = {
      path,
      pathKey,
      index,
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
      element,
      timerId: window.setTimeout(() => {
        setDragState({
          pathKey,
          fromIndex: index,
          overIndex: index,
          mode: "touch",
        });
      }, LONG_PRESS_MS),
    };

    touchDragMetaRef.current = meta;
  }

  function handleArrayItemPointerMove(pathKey, event) {
    const meta = touchDragMetaRef.current;

    if (
      !meta ||
      meta.pointerId !== event.pointerId ||
      meta.pathKey !== pathKey
    ) {
      return;
    }

    const deltaX = event.clientX - meta.originX;
    const deltaY = event.clientY - meta.originY;
    const distance = Math.hypot(deltaX, deltaY);

    if (!dragState || dragState.mode !== "touch") {
      if (distance > TOUCH_DRAG_CANCEL_DISTANCE) {
        clearTouchDrag();
      }
      return;
    }

    event.preventDefault();

    const dropTarget = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest("[data-array-path-key]");
    if (!dropTarget || dropTarget.dataset.arrayPathKey !== pathKey) {
      return;
    }

    const overIndex = Number(dropTarget.dataset.arrayIndex);
    if (!Number.isInteger(overIndex)) {
      return;
    }

    setDragState((current) => {
      if (
        !current ||
        current.pathKey !== pathKey ||
        current.overIndex === overIndex
      ) {
        return current;
      }

      return { ...current, overIndex };
    });
  }

  function handleArrayItemPointerUp(event) {
    const meta = touchDragMetaRef.current;

    if (!meta || meta.pointerId !== event.pointerId) {
      return;
    }

    if (meta.timerId) {
      window.clearTimeout(meta.timerId);
    }

    const activeDrag =
      dragState &&
      dragState.mode === "touch" &&
      dragState.pathKey === meta.pathKey
        ? dragState
        : null;

    releasePointerCapture(meta);
    touchDragMetaRef.current = null;

    if (activeDrag) {
      event.preventDefault();

      if (activeDrag.fromIndex !== activeDrag.overIndex) {
        moveArrayItem(meta.path, activeDrag.fromIndex, activeDrag.overIndex);
      }
    }

    setDragState((current) => (current?.mode === "touch" ? null : current));
  }

  function clearDragState() {
    clearTouchDrag();
    setDragState(null);
  }

  async function uploadAsset(path, file) {
    const pathKey = path.join(".");
    const fileNameBase = `${slugify(draftContent.site.displayName || draftContent.site.companyName || site.display_name)}-${pathKey.replace(/\./g, "-")}`;
    const dataUrl = await readFileAsDataUrl(file);

    setUploading((current) => ({ ...current, [pathKey]: true }));
    setPublishState({ status: "idle", message: "" });

    try {
      const payload = await apiFetch(`/sites/${site.site_id}/assets`, {
        accessToken,
        method: "POST",
        body: {
          fileNameBase,
          image: {
            type: "base64",
            value: dataUrl,
          },
        },
      });

      const targetPath =
        path[path.length - 1] === "logoUrl" ? path : [...path, "url"];
      updateValue(targetPath, payload.data.path);
      setPreviewSources((current) => ({
        ...current,
        [payload.data.path]: payload.data.previewUrl || dataUrl,
      }));
    } catch (requestError) {
      setPublishState({
        status: "error",
        message: requestError.message || "Bilduppladdningen misslyckades.",
      });
    } finally {
      setUploading((current) => ({ ...current, [pathKey]: false }));
    }
  }

  async function handlePublish() {
    if (!changedPatch || !Object.keys(changedPatch).length) {
      return;
    }

    setPublishState({ status: "saving", message: "" });

    try {
      const payload = await apiFetch(`/sites/${site.site_id}/content/home`, {
        accessToken,
        method: "POST",
        body: changedPatch,
      });

      setOriginalContent(payload.data);
      setDraftContent(payload.data);
      setMeta(payload.meta);
      setTrackedCommitSha(payload.meta.commitSha);
      setPublishState({
        status: "success",
        message: `Publicerad. Commit ${payload.meta.commitSha.slice(0, 7)} skapad.`,
      });
    } catch (requestError) {
      const fallbackMessage =
        requestError.status === 409
          ? "Innehållet hann ändras i GitHub. Ladda om och försök igen."
          : requestError.message || "Publiceringen misslyckades.";
      setPublishState({ status: "error", message: fallbackMessage });
    }
  }

  function handleFieldFocus(pathKey) {
    setActiveFieldPath(pathKey);
  }

  function registerFieldRef(pathKey, element) {
    if (!element) {
      fieldRefs.current.delete(pathKey);
      return;
    }

    fieldRefs.current.set(pathKey, element);
  }

  function focusField(pathKey) {
    const sectionKey = String(pathKey || "").split(".")[0];

    if (
      SECTION_ORDER.includes(sectionKey) &&
      sectionKey !== resolvedActiveSection
    ) {
      onActiveSectionChange?.(sectionKey);
    }

    window.setTimeout(() => {
      const element = fieldRefs.current.get(pathKey);

      if (!element) {
        return;
      }

      setActiveFieldPath(pathKey);
      element.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
      element.focus({ preventScroll: true });
    }, 120);
  }

  function renderScalarField(path, value, options = {}) {
    const key = path[path.length - 1];
    const keyName = String(key);
    const pathKey = path.join(".");
    const disabled = options.disabled || false;

    if (pathKey === "hero.primaryCtaHref") {
      return null;
    }

    if (pathKey === "site.headingFont" || pathKey === "site.bodyFont") {
      const fallbackValue =
        pathKey === "site.headingFont"
          ? 'Georgia, "Times New Roman", serif'
          : 'Arial, sans-serif';
      const label =
        pathKey === "site.headingFont" ? "Rubriktypsnitt" : "Texttypsnitt";

      return (
        <label key={pathKey} className="field">
          <span>{label}</span>
          <select
            value={value || fallbackValue}
            ref={(element) => registerFieldRef(pathKey, element)}
            onChange={(event) => updateValue(path, event.target.value)}
            onFocus={() => handleFieldFocus(pathKey)}
          >
            {FONT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (pathKey === "site.theme" || pathKey === "site.themeMode") {
      const optionsList =
        pathKey === "site.theme" ? THEME_OPTIONS : THEME_MODE_OPTIONS;
      const fallbackValue = pathKey === "site.theme" ? "classic" : "light";
      const label = pathKey === "site.theme" ? "Tema" : "Färgläge";

      return (
        <label key={pathKey} className="field">
          <span>{label}</span>
          <select
            value={value || fallbackValue}
            ref={(element) => registerFieldRef(pathKey, element)}
            onChange={(event) => updateValue(path, event.target.value)}
            onFocus={() => handleFieldFocus(pathKey)}
          >
            {optionsList.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (typeof value === "boolean") {
      return (
        <label key={pathKey} className="toggle-field">
          <input
            type="checkbox"
            checked={value}
            ref={(element) => registerFieldRef(pathKey, element)}
            onChange={(event) => updateValue(path, event.target.checked)}
            onFocus={() => handleFieldFocus(pathKey)}
          />
          <span>{toLabel(keyName)}</span>
        </label>
      );
    }

    if (keyName.toLowerCase().includes("color")) {
      const pickerValue = /^#[0-9a-f]{6}$/i.test(value || "")
        ? value
        : COLOR_PICKER_FALLBACKS[pathKey] || "#000000";

      return (
        <div key={pathKey} className="field field--color">
          <label>
            <span>{toLabel(keyName)}</span>
            <input
              type="text"
              value={value || ""}
              disabled={disabled}
              ref={(element) => registerFieldRef(pathKey, element)}
              onChange={(event) => updateValue(path, event.target.value)}
              onFocus={() => handleFieldFocus(pathKey)}
            />
          </label>
          <input
            type="color"
            value={pickerValue}
            disabled={disabled}
            onChange={(event) => updateValue(path, event.target.value)}
            onFocus={() => handleFieldFocus(pathKey)}
            aria-label={toLabel(keyName)}
          />
        </div>
      );
    }

    const InputTag = fieldUsesTextarea(keyName) ? "textarea" : "input";
    const extraProps = InputTag === "textarea" ? { rows: 4 } : { type: "text" };

    return (
      <label key={pathKey} className="field">
        <span>{toLabel(keyName)}</span>
        <InputTag
          {...extraProps}
          value={value ?? ""}
          disabled={disabled}
          ref={(element) => registerFieldRef(pathKey, element)}
          onChange={(event) => updateValue(path, event.target.value)}
          onFocus={() => handleFieldFocus(pathKey)}
        />
      </label>
    );
  }

  function renderImageField(path, value, { label, logo = false } = {}) {
    const pathKey = path.join(".");
    const imageUrl = logo ? value : value?.url;
    const resolvedUrl =
      previewSources[imageUrl] || resolveSiteAssetUrl(site, imageUrl);

    return (
      <div key={pathKey} className="image-field shell-subcard">
        <div className="image-field__header">
          <div>
            <h4>{label || toLabel(path[path.length - 1])}</h4>
            <p>
              Nuvarande värde är förifyllt och kan ersättas med filuppladdning.
            </p>
          </div>
          <label className="button button--ghost button--small image-field__upload">
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={Boolean(uploading[pathKey])}
              onChange={(event) => {
                const nextFile = event.target.files?.[0];
                if (nextFile) {
                  uploadAsset(path, nextFile);
                }
                event.target.value = "";
              }}
            />
            {uploading[pathKey] ? "Laddar upp..." : "Ladda upp bild"}
          </label>
        </div>
        <div className="image-field__body">
          <div className="image-field__preview">
            {resolvedUrl ? (
              <img src={resolvedUrl} alt={logo ? label : value?.alt || label} />
            ) : (
              <span>Ingen bild vald</span>
            )}
          </div>
          <div className="image-field__controls">
            {logo ? (
              renderScalarField(path, value ?? "")
            ) : (
              <>
                {renderScalarField([...path, "url"], value?.url || "")}
                {renderScalarField([...path, "alt"], value?.alt || "")}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderArray(path, value) {
    const pathKey = path.join(".");
    if (pathKey === "openingHours.days") {
      return renderOpeningHoursDaysField(path, value);
    }

    const isStringArray = value.every((item) => typeof item === "string");
    const sortable = isSortableArray(pathKey);

    return (
      <div key={pathKey} className="array-field shell-subcard">
        <div className="array-field__header">
          <div>
            <h4>{toLabel(path[path.length - 1])}</h4>
            <p>
              {sortable
                ? "Dra posterna för att ändra ordningen. På mobil: håll inne ett kort och dra."
                : "Alla befintliga poster visas direkt i formuläret."}
            </p>
          </div>
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => addArrayItem(path)}
          >
            Lägg till
          </button>
        </div>
        <div className="array-field__items">
          {value.map((item, index) => {
            const itemKey = buildArrayItemKey(pathKey, index);
            const isDragged =
              dragState?.pathKey === pathKey && dragState?.fromIndex === index;
            const isDropTarget =
              dragState?.pathKey === pathKey && dragState?.overIndex === index;
            const isTouchArmed =
              dragState?.mode === "touch" &&
              dragState?.pathKey === pathKey &&
              dragState?.fromIndex === index;
            const isNewItem = highlightedArrayItemKey === itemKey;

            return (
              <div
                key={itemKey}
                ref={(element) => registerArrayItemRef(itemKey, element)}
                className={`array-item ${sortable ? "array-item--sortable" : ""} ${isDragged ? "array-item--dragging" : ""} ${isDropTarget ? "array-item--drop-target" : ""} ${isTouchArmed ? "array-item--touch-armed" : ""} ${isNewItem ? "array-item--new" : ""}`}
                data-array-path-key={pathKey}
                data-array-index={index}
                draggable={sortable}
                onDragStart={
                  sortable ? () => handleDragStart(pathKey, index) : undefined
                }
                onDragEnter={
                  sortable ? () => handleDragEnter(pathKey, index) : undefined
                }
                onDragOver={
                  sortable ? (event) => event.preventDefault() : undefined
                }
                onDrop={
                  sortable ? () => handleDrop(path, pathKey, index) : undefined
                }
                onDragEnd={sortable ? clearDragState : undefined}
                onPointerDown={
                  sortable
                    ? (event) =>
                        handleArrayItemPointerDown(path, pathKey, index, event)
                    : undefined
                }
                onPointerMove={
                  sortable
                    ? (event) => handleArrayItemPointerMove(pathKey, event)
                    : undefined
                }
                onPointerUp={sortable ? handleArrayItemPointerUp : undefined}
                onPointerCancel={sortable ? clearTouchDrag : undefined}
              >
                <div className="array-item__content">
                  {isStringArray
                    ? renderScalarField([...path, index], item)
                    : pathKey === "media.gallery"
                      ? renderImageField([...path, index], item, {
                          label: `Galleri ${index + 1}`,
                        })
                      : Object.entries(item).map(([childKey, childValue]) =>
                          renderNode([...path, index, childKey], childValue),
                        )}
                </div>
                <div className="array-item__actions">
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => removeArrayItem(path, index)}
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            );
          })}
          {!value.length ? (
            <p className="muted-text">Ingen post ännu.</p>
          ) : null}
        </div>
      </div>
    );
  }

  function normalizeOpeningHoursDays(value) {
    const sourceDays = Array.isArray(value) ? value : [];

    return WEEKDAYS.map((day, index) => {
      const match = sourceDays.find((item) => item?.day === day) || sourceDays[index] || {};

      return {
        day,
        opens: match.opens || "",
        closes: match.closes || "",
        closed: Boolean(match.closed),
      };
    });
  }

  function updateOpeningHoursDay(path, index, key, nextValue) {
    const currentDays = normalizeOpeningHoursDays(getIn(draftContent, path));
    updateValue(
      path,
      currentDays.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [key]: key === "closed" ? Boolean(nextValue) : nextValue,
            }
          : item,
      ),
    );
  }

  function renderOpeningHoursDaysField(path, value) {
    const pathKey = path.join(".");
    const days = normalizeOpeningHoursDays(value);

    return (
      <div key={pathKey} className="array-field opening-hours-field shell-subcard">
        <div className="array-field__header">
          <div>
            <h4>Veckans öppettider</h4>
            <p>Fyll i öppnings- och stängningstid för varje dag, eller markera dagen som stängd.</p>
          </div>
        </div>
        <div className="opening-hours-field__rows">
          {days.map((item, index) => (
            <div key={item.day} className={`opening-hours-field__row ${item.closed ? "opening-hours-field__row--closed" : ""}`}>
              <div className="opening-hours-field__day">{item.day}</div>
              <label className="field">
                <span>Öppnar</span>
                <input
                  type="text"
                  value={item.opens}
                  placeholder="10.00"
                  disabled={item.closed}
                  ref={(element) => registerFieldRef(`${pathKey}.${index}.opens`, element)}
                  onChange={(event) => updateOpeningHoursDay(path, index, "opens", event.target.value)}
                  onFocus={() => handleFieldFocus(`${pathKey}.${index}.opens`)}
                />
              </label>
              <label className="field">
                <span>Stänger</span>
                <input
                  type="text"
                  value={item.closes}
                  placeholder="17.00"
                  disabled={item.closed}
                  ref={(element) => registerFieldRef(`${pathKey}.${index}.closes`, element)}
                  onChange={(event) => updateOpeningHoursDay(path, index, "closes", event.target.value)}
                  onFocus={() => handleFieldFocus(`${pathKey}.${index}.closes`)}
                />
              </label>
              <label className="toggle-field opening-hours-field__toggle">
                <input
                  type="checkbox"
                  checked={item.closed}
                  ref={(element) => registerFieldRef(`${pathKey}.${index}.closed`, element)}
                  onChange={(event) => updateOpeningHoursDay(path, index, "closed", event.target.checked)}
                  onFocus={() => handleFieldFocus(`${pathKey}.${index}.closed`)}
                />
                <span>Stängt</span>
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderSocialLinksField(path, value) {
    const pathKey = path.join(".");
    const entries = Object.entries(value || {});

    return (
      <div key={pathKey} className="array-field social-links-field shell-subcard">
        <div className="array-field__header">
          <div>
            <h4>Sociala länkar</h4>
            <p>Dra länkarna för att ändra ordningen. Namnet visas som länktext i footern.</p>
          </div>
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => addSocialLink(path)}
          >
            Lägg till
          </button>
        </div>
        <div className="array-field__items">
          {entries.map(([key, url], index) => {
            const isDragged =
              dragState?.pathKey === pathKey && dragState?.fromIndex === index;
            const isDropTarget =
              dragState?.pathKey === pathKey && dragState?.overIndex === index;
            const focusPath = `${pathKey}.${key}`;

            return (
              <div
                key={`${key}-${index}`}
                className={`array-item social-link-item array-item--sortable ${isDragged ? "array-item--dragging" : ""} ${isDropTarget ? "array-item--drop-target" : ""}`}
                draggable
                onDragStart={() => handleDragStart(pathKey, index)}
                onDragEnter={() => handleDragEnter(pathKey, index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleSocialLinkDrop(path, pathKey, index)}
                onDragEnd={clearDragState}
              >
                <span className="social-link-item__handle" aria-hidden="true">::</span>
                <label className="field">
                  <span>Namn</span>
                  <input
                    type="text"
                    defaultValue={key}
                    onBlur={(event) => renameSocialLink(path, key, event.target.value)}
                    onFocus={() => handleFieldFocus(focusPath)}
                  />
                </label>
                <label className="field">
                  <span>URL</span>
                  <input
                    type="text"
                    value={url || ""}
                    ref={(element) => registerFieldRef(`${focusPath}:url`, element)}
                    onChange={(event) => updateSocialLinkUrl(path, key, event.target.value)}
                    onFocus={() => handleFieldFocus(focusPath)}
                  />
                </label>
                <div className="array-item__actions">
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => removeSocialLink(path, key)}
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            );
          })}
          {!entries.length ? <p className="muted-text">Inga sociala länkar ännu.</p> : null}
        </div>
      </div>
    );
  }

  function renderObject(path, value) {
    const pathKey = path.join(".");
    const key = path[path.length - 1];

    if (pathKey === "footer.socialLinks") {
      return renderSocialLinksField(path, value);
    }

    if (
      pathKey === "media.heroImage" ||
      pathKey === "media.aboutImage" ||
      /^services\.items\.\d+\.image$/.test(pathKey)
    ) {
      return renderImageField(path, value, { label: toLabel(key) });
    }

    return (
      <section key={pathKey} className="editor-section shell-subcard">
        <div className="section-heading">
          <h3>{toLabel(key)}</h3>
        </div>
        <div className="section-fields">
          {Object.entries(value).map(([childKey, childValue]) =>
            renderNode([...path, childKey], childValue),
          )}
        </div>
      </section>
    );
  }

  function renderNode(path, value) {
    const pathKey = path.join(".");

    if (pathKey === "site.siteId") {
      return renderScalarField(path, value, { disabled: true });
    }

    if (pathKey === "media.logoUrl") {
      return renderImageField(path, value, { label: "Logo", logo: true });
    }

    if (Array.isArray(value)) {
      return renderArray(path, value);
    }

    if (value && typeof value === "object") {
      return renderObject(path, value);
    }

    return renderScalarField(path, value);
  }

  if (loading) {
    return (
      <div className="shell-card">
        <p>Laddar innehåll...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shell-card">
        <p className="feedback feedback--error">{error}</p>
      </div>
    );
  }

  return (
    <div className="editor-layout">
      <section className="editor-panel shell-card">
        <div className="editor-toolbar">
          <div>
            <p className="eyebrow">{draftContent.site.displayName}</p>
            <h2>
              {SECTION_LABELS[resolvedActiveSection] ||
                toLabel(resolvedActiveSection)}
            </h2>
          </div>
          <div className="editor-toolbar__actions">
            {meta?.commitSha ? (
              <span className="status-pill">
                Senaste commit: {meta.commitSha.slice(0, 7)}
              </span>
            ) : null}
          </div>
        </div>

        {publishState.message ? (
          <p
            className={`feedback ${publishState.status === "success" ? "feedback--success" : "feedback--error"}`}
          >
            {publishState.message}
          </p>
        ) : null}

        <WorkflowStatusPanel
          siteId={site.site_id}
          accessToken={accessToken}
          commitSha={trackedCommitSha}
          visible={Boolean(trackedCommitSha)}
        />

        <div
          id={`editor-section-${resolvedActiveSection}`}
          className="editor-sections editor-sections--tabbed"
          role="tabpanel"
          aria-label={
            SECTION_LABELS[resolvedActiveSection] ||
            toLabel(resolvedActiveSection)
          }
        >
          {renderNode(
            [resolvedActiveSection],
            draftContent[resolvedActiveSection],
          )}
        </div>

        <div className="editor-footer">
          <span className="muted-text">
            {hasChanges
              ? "Opublicerade ändringar finns."
              : "Inga opublicerade ändringar."}
          </span>
          <button
            type="button"
            className="button button--primary"
            disabled={!hasChanges || publishState.status === "saving"}
            onClick={handlePublish}
          >
            {publishState.status === "saving" ? "Publicerar..." : "Publicera"}
          </button>
        </div>
      </section>
      <PreviewPane
        site={site}
        content={draftContent}
        previewSources={previewSources}
        activeFieldPath={activeFieldPath}
        onSelectField={focusField}
        accessToken={accessToken}
      />
    </div>
  );
}
